<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { NButton, NDrawer, NDrawerContent, NInput, NTabs, NTabPane } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useSourcesStore } from '../stores/sources'
import { useIsMobile } from '../composables/useIsMobile'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'
import AddToWikiFilePane from './AddToWikiFilePane.vue'
import IngestActivityList from './IngestActivityList.vue'

const show = defineModel<boolean>('show', { required: true })

useRestoreFocusOnClose(show)

const sources = useSourcesStore()
const router = useRouter()
const isMobile = useIsMobile()

// On mobile the drawer occupies the full screen — a fixed 520px drawer
// on a 390px phone viewport was the source of the side-scroll.
const DESKTOP_DRAWER_WIDTH = 520
const drawerWidth = computed<number | string>(() => (isMobile.value ? '100%' : DESKTOP_DRAWER_WIDTH))

const { submitting, submitError } = storeToRefs(sources)

// The internal tab key stays `ask` (the store's `submitAsk`, the
// `tab-ask` testid the e2e suite filters on); only the visible label
// reads "Note", since the tab submits free text to Ingest.
const tab = ref<'ask' | 'file' | 'url'>('ask')
const askContent = ref('')
// Optional caller-supplied write-target pin on the Note surface. Empty
// string (= "Let Ingest decide") means "no category, let the LLM
// auto-classify" — the existing behaviour; the store's submitAsk omits
// the key for an empty string. A native <select> (as in the design) so
// the visible label is a real <label for>. The five non-empty values
// mirror Wiki::Structure::CATEGORY_MAP.
const askCategory = ref<string>('')
const CATEGORY_OPTIONS = [
  { label: 'Let Ingest decide', value: '' },
  { label: 'Entities', value: 'entities' },
  { label: 'Concepts', value: 'concepts' },
  { label: 'Summaries', value: 'summaries' },
  { label: 'Templates', value: 'templates' },
  { label: 'Memory', value: 'memory' },
]
const urlContent = ref('')

const canSubmitAsk = computed(() => askContent.value.trim().length > 0 && !submitting.value)
const canSubmitUrl = computed(() => urlContent.value.trim().length > 0 && !submitting.value)

async function onSubmitAsk() {
  if (!canSubmitAsk.value) return
  try {
    await sources.submitAsk(askContent.value.trim(), askCategory.value)
    askContent.value = ''
    // Keep the category selection after submit — the user's most likely
    // next intent is "ingest another note in the same bucket."
  } catch {
    /* submitError populated in store */
  }
}

// Cmd+Enter / Ctrl+Enter sends — matches the ThreadDrawer composer and
// every chat UI the user touches daily. Plain Enter stays as newline.
function onAskEnter(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  if (!event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  void onSubmitAsk()
}

async function onSubmitUrl() {
  if (!canSubmitUrl.value) return
  try {
    await sources.submitUrl(urlContent.value.trim())
    urlContent.value = ''
  } catch {
    /* submitError populated in store */
  }
}

function openPath(path: string) {
  // Plain path keeps slashes intact — named routes percent-encode them.
  router.push(`/p/${path}`)
  show.value = false
}

function openLog() {
  router.push('/log')
  show.value = false
}
</script>

<template>
  <NDrawer v-model:show="show" :width="drawerWidth" placement="right">
    <NDrawerContent :native-scrollbar="false" closable>
      <template #header>
        <h2 class="m-0 font-serif text-[28px] font-medium leading-tight text-ink">Add to brain</h2>
      </template>

      <p class="m-0 mb-5 leading-normal text-ink-muted">
        Ingest reads it, updates the wiki pages it touches, refreshes cross-references and logs the change in one
        commit.
      </p>

      <NTabs v-model:value="tab" type="segment" animated class="ingest-tabs" aria-label="What to add">
        <NTabPane name="ask">
          <template #tab><span data-testid="tab-ask">Note</span></template>
          <form class="flex flex-col gap-4 pt-4" @submit.prevent="onSubmitAsk">
            <div class="flex flex-col gap-2">
              <label for="atw-note" class="font-medium text-ink">What should the brain know?</label>
              <NInput
                v-model:value="askContent"
                type="textarea"
                :autosize="{ minRows: 6, maxRows: 12 }"
                placeholder="Paste text, write a decision, or describe something you learned…"
                :input-props="{ id: 'atw-note', spellcheck: true }"
                class="text-[15px]"
                data-testid="prompt-content"
                @keydown="onAskEnter"
              />
            </div>
            <!-- Optional pin for the LLM's write target. "Let Ingest
                 decide" preserves the existing auto-classify behaviour. -->
            <div class="flex flex-col gap-2">
              <label for="atw-target" class="font-medium text-ink">
                Save into <span class="font-normal text-ink-faint">optional</span>
              </label>
              <select
                id="atw-target"
                v-model="askCategory"
                class="min-h-11 w-full rounded-[10px] border border-border-control bg-surface-bg px-3 text-ink"
                data-testid="prompt-category"
              >
                <option v-for="o in CATEGORY_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-3">
              <p v-if="submitError" class="m-0 mr-auto text-sm text-status-alert">{{ submitError }}</p>
              <span class="text-xs text-ink-faint">⌘/Ctrl ↵ to send</span>
              <NButton
                type="primary"
                attr-type="submit"
                class="min-h-11 px-5 font-semibold"
                :loading="submitting"
                :disabled="!canSubmitAsk"
                data-testid="prompt-submit"
              >
                Ingest
              </NButton>
            </div>
          </form>
        </NTabPane>

        <NTabPane name="file">
          <template #tab><span data-testid="tab-file">File</span></template>
          <AddToWikiFilePane />
        </NTabPane>

        <NTabPane name="url">
          <template #tab><span data-testid="tab-url">URL</span></template>
          <form class="flex flex-col gap-4 pt-4" @submit.prevent="onSubmitUrl">
            <div class="flex flex-col gap-2">
              <label for="atw-url" class="font-medium text-ink">Link to fetch</label>
              <NInput
                v-model:value="urlContent"
                size="large"
                placeholder="https://example.com/paper.pdf"
                :input-props="{ id: 'atw-url', inputmode: 'url' }"
                data-testid="url-content"
              />
            </div>
            <div class="flex flex-wrap items-center justify-end gap-3">
              <p v-if="submitError" class="m-0 mr-auto text-sm text-status-alert">{{ submitError }}</p>
              <NButton
                type="primary"
                attr-type="submit"
                class="min-h-11 px-5 font-semibold"
                :loading="submitting"
                :disabled="!canSubmitUrl"
                data-testid="url-submit"
              >
                Ingest
              </NButton>
            </div>
          </form>
        </NTabPane>
      </NTabs>

      <!-- Activity list: shared across all tabs, extracted to
           IngestActivityList (#1099) so this view stays under the
           300-line hard limit. -->
      <IngestActivityList @open-path="openPath" @open-log="openLog" />
    </NDrawerContent>
  </NDrawer>
</template>

<style scoped>
/* WCAG 2.5.5 — touch targets at least 44×44 CSS pixels. The default
   segment-tab height (~32px on Naive UI 2.44) skirts the WCAG floor on
   mobile, where each tab takes ~30% of a 390px viewport. */
.ingest-tabs :deep(.n-tabs-tab) {
  min-height: 44px;
  font-weight: 500;
}
.ingest-tabs :deep(.n-tabs-tab--active) {
  font-weight: 600;
}
</style>
